import React, { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Navigation, Clock } from "lucide-react";

// Parish coordinates for simulated routing
const PARISH_COORDS = {
  Kingston: [17.9714, -76.7936], "Montego Bay": [18.4762, -77.8939], Portmore: [17.9500, -76.8800],
  "Spanish Town": [17.9910, -76.9570], Mandeville: [18.0430, -77.5070], "Ocho Rios": [18.4074, -77.1030],
  Negril: [18.2690, -78.3480],
};
const WAREHOUSE_COORDS = { KIN: [17.9900, -76.7800], MBJ: [18.4900, -77.9100], OCR: [18.4100, -77.1100] };

const truckIcon = L.divIcon({
  className: "",
  html: '<div style="background:#E11B22;width:34px;height:34px;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 12px rgba(0,0,0,.3);border:3px solid #fff"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10 17h4V5H2v12h3"/><path d="M20 17h2v-3.34a4 4 0 0 0-1.17-2.83L19 9h-5v8h1"/><circle cx="7.5" cy="17.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></svg></div>',
  iconSize: [34, 34], iconAnchor: [17, 17],
});
const homeIcon = L.divIcon({
  className: "",
  html: '<div style="background:#0B131F;width:30px;height:30px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;box-shadow:0 4px 12px rgba(0,0,0,.3);border:2px solid #fff"><div style="transform:rotate(45deg);color:#fff;font-size:14px">📍</div></div>',
  iconSize: [30, 30], iconAnchor: [15, 30],
});

function FitBounds({ a, b }) {
  const map = useMap();
  useEffect(() => { map.fitBounds([a, b], { padding: [50, 50] }); }, [map, a, b]);
  return null;
}

// Rough distance in metres between two [lat,lng] points (haversine)
function distMeters(a, b) {
  const R = 6371000, toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]), dLng = toRad(b[1] - a[1]);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

const GEOFENCE_M = 1200; // arrival geofence radius around the delivery address

export function DriverMap({ order }) {
  const dest = PARISH_COORDS[order.address?.parish] || PARISH_COORDS.Kingston;
  const start = WAREHOUSE_COORDS[order.warehouse] || WAREHOUSE_COORDS.KIN;
  const outForDelivery = order.status === "out_for_delivery";
  const delivered = order.status === "delivered";

  const [progress, setProgress] = useState(delivered ? 1 : 0.05);
  useEffect(() => {
    if (!outForDelivery) { setProgress(delivered ? 1 : 0.05); return; }
    const t = setInterval(() => setProgress((p) => Math.min(1, p + 0.012)), 1000);
    return () => clearInterval(t);
  }, [outForDelivery, delivered]);

  const pos = [start[0] + (dest[0] - start[0]) * progress, start[1] + (dest[1] - start[1]) * progress];
  const etaMin = Math.max(1, Math.round((1 - progress) * 25));
  const meters = distMeters(pos, dest);
  const insideGeofence = delivered || meters <= GEOFENCE_M;

  return (
    <div className="rounded-2xl overflow-hidden border border-border" data-testid="driver-tracking-map">
      <div className="flex items-center justify-between bg-ink text-white px-4 py-2.5">
        <div className="flex items-center gap-2 text-sm font-semibold"><Navigation size={16} className="text-emerald" /> {order.driver_name || "Driver"} en route</div>
        <div className={`flex items-center gap-1.5 text-sm font-mono-sn px-2.5 py-1 rounded-full ${insideGeofence ? "bg-emerald text-white" : "bg-white/10"}`} data-testid="geofence-status">
          <Clock size={13} /> {delivered ? "Delivered" : insideGeofence ? "Arriving now" : `${etaMin} min`}
        </div>
      </div>
      {!delivered && (
        <div className={`px-4 py-1.5 text-xs font-semibold ${insideGeofence ? "bg-emerald/15 text-emerald" : "bg-secondary text-muted-foreground"}`} data-testid="geofence-banner">
          {insideGeofence
            ? "📍 Your driver has entered your delivery area — be ready to receive your order."
            : `Your driver is about ${(meters / 1000).toFixed(1)} km away from your address.`}
        </div>
      )}
      <MapContainer center={pos} zoom={11} style={{ height: 260, width: "100%" }} scrollWheelZoom={false}>
        <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" attribution="&copy; OpenStreetMap &copy; CARTO" />
        <Circle center={dest} radius={GEOFENCE_M} pathOptions={{ color: insideGeofence ? "#0FA958" : "#94a3b8", fillColor: insideGeofence ? "#0FA958" : "#94a3b8", fillOpacity: 0.12, weight: 1.5 }} />
        <Polyline positions={[start, dest]} pathOptions={{ color: "#94a3b8", weight: 3, dashArray: "6 8" }} />
        <Polyline positions={[start, pos]} pathOptions={{ color: "#E11B22", weight: 4 }} />
        <Marker position={pos} icon={truckIcon} />
        <Marker position={dest} icon={homeIcon} />
        <FitBounds a={start} b={dest} />
      </MapContainer>
    </div>
  );
}
